/**
 * Settings Page
 *
 * System settings management for SADMIN users only.
 */

import { useState } from 'react';
import {
  Bell,
  Shield,
  Database,
  Globe,
  Mail,
  Server,
  Save,
} from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Toggle,
} from '@/components/common';
import { useToast } from '@/hooks/useToast';

// ============================================
// Settings Section Component
// ============================================

interface SettingsSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SettingsSection({ title, description, icon, children }: SettingsSectionProps) {
  return (
    <Card>
      <CardHeader description={description}>
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-lg font-semibold text-foreground dark:text-foreground-dark">
            {title}
          </span>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">{children}</CardBody>
    </Card>
  );
}

// ============================================
// Setting Row Component
// ============================================

interface SettingRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function SettingRow({ label, description, children }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border dark:border-border-dark last:border-0">
      <div>
        <p className="font-medium text-foreground dark:text-foreground-dark">{label}</p>
        {description && (
          <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
            {description}
          </p>
        )}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

// ============================================
// Page Component
// ============================================

export function SettingsPage() {
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Notification settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [loginAlerts, setLoginAlerts] = useState(true);
  const [systemAlerts, setSystemAlerts] = useState(true);

  // Security settings
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState('30');
  const [ipWhitelist, setIpWhitelist] = useState(false);

  // System settings
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [apiRateLimit, setApiRateLimit] = useState('1000');

  // Email settings
  const [smtpHost, setSmtpHost] = useState('smtp.example.com');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('Settings saved successfully');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            System Settings
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Configure system-wide settings and preferences
          </p>
        </div>
        <Button onClick={handleSaveSettings} loading={isSaving}>
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>

      {/* Settings Sections */}
      <div className="grid gap-6">
        {/* Notification Settings */}
        <SettingsSection
          title="Notifications"
          description="Configure how you receive notifications"
          icon={<Bell className="h-5 w-5 text-primary" />}
        >
          <SettingRow
            label="Email Notifications"
            description="Receive important updates via email"
          >
            <Toggle checked={emailNotifications} onChange={setEmailNotifications} />
          </SettingRow>
          <SettingRow
            label="SMS Notifications"
            description="Receive critical alerts via SMS"
          >
            <Toggle checked={smsNotifications} onChange={setSmsNotifications} />
          </SettingRow>
          <SettingRow
            label="Login Alerts"
            description="Get notified of new login activities"
          >
            <Toggle checked={loginAlerts} onChange={setLoginAlerts} />
          </SettingRow>
          <SettingRow
            label="System Alerts"
            description="Receive system health notifications"
          >
            <Toggle checked={systemAlerts} onChange={setSystemAlerts} />
          </SettingRow>
        </SettingsSection>

        {/* Security Settings */}
        <SettingsSection
          title="Security"
          description="Manage security and authentication settings"
          icon={<Shield className="h-5 w-5 text-primary" />}
        >
          <SettingRow
            label="Two-Factor Authentication"
            description="Require 2FA for all admin users"
          >
            <Toggle checked={twoFactorAuth} onChange={setTwoFactorAuth} />
          </SettingRow>
          <SettingRow
            label="Session Timeout (minutes)"
            description="Auto-logout after inactivity"
          >
            <Input
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(e.target.value)}
              type="number"
              className="w-24"
            />
          </SettingRow>
          <SettingRow
            label="IP Whitelist"
            description="Restrict access to whitelisted IPs only"
          >
            <Toggle checked={ipWhitelist} onChange={setIpWhitelist} />
          </SettingRow>
        </SettingsSection>

        {/* System Settings */}
        <SettingsSection
          title="System"
          description="General system configuration"
          icon={<Server className="h-5 w-5 text-primary" />}
        >
          <SettingRow
            label="Maintenance Mode"
            description="Enable maintenance mode for all users"
          >
            <Toggle checked={maintenanceMode} onChange={setMaintenanceMode} />
          </SettingRow>
          <SettingRow
            label="Debug Mode"
            description="Enable detailed error logging"
          >
            <Toggle checked={debugMode} onChange={setDebugMode} />
          </SettingRow>
          <SettingRow
            label="API Rate Limit (requests/hour)"
            description="Maximum API requests per hour per user"
          >
            <Input
              value={apiRateLimit}
              onChange={(e) => setApiRateLimit(e.target.value)}
              type="number"
              className="w-24"
            />
          </SettingRow>
        </SettingsSection>

        {/* Email Configuration */}
        <SettingsSection
          title="Email Configuration"
          description="SMTP server settings for outgoing emails"
          icon={<Mail className="h-5 w-5 text-primary" />}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="SMTP Host"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.example.com"
            />
            <Input
              label="SMTP Port"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              placeholder="587"
            />
            <Input
              label="SMTP Username"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder="user@example.com"
            />
            <Input
              label="SMTP Password"
              type="password"
              placeholder="Enter password"
            />
          </div>
        </SettingsSection>

        {/* Regional Settings */}
        <SettingsSection
          title="Regional Settings"
          description="Locale and formatting preferences"
          icon={<Globe className="h-5 w-5 text-primary" />}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-foreground-dark mb-1">
                Timezone
              </label>
              <select className="w-full px-3 py-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-foreground dark:text-foreground-dark">
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-foreground-dark mb-1">
                Date Format
              </label>
              <select className="w-full px-3 py-2 border border-border dark:border-border-dark rounded-md bg-background dark:bg-background-dark text-foreground dark:text-foreground-dark">
                <option value="dd-MM-yyyy">dd-MM-yyyy</option>
                <option value="MM-dd-yyyy">MM-dd-yyyy</option>
                <option value="yyyy-MM-dd">yyyy-MM-dd</option>
              </select>
            </div>
          </div>
        </SettingsSection>

        {/* Database Settings */}
        <SettingsSection
          title="Database"
          description="Database maintenance and backup settings"
          icon={<Database className="h-5 w-5 text-primary" />}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-background-subtle dark:bg-background-dark-subtle rounded-lg">
              <div>
                <p className="font-medium text-foreground dark:text-foreground-dark">
                  Database Backup
                </p>
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                  Last backup: 22-01-2026 02:00:00
                </p>
              </div>
              <Button variant="outline" size="sm">
                Backup Now
              </Button>
            </div>
            <div className="flex items-center justify-between p-4 bg-background-subtle dark:bg-background-dark-subtle rounded-lg">
              <div>
                <p className="font-medium text-foreground dark:text-foreground-dark">
                  Clear Cache
                </p>
                <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                  Clear application cache to free up memory
                </p>
              </div>
              <Button variant="outline" size="sm">
                Clear Cache
              </Button>
            </div>
          </div>
        </SettingsSection>
      </div>
    </div>
  );
}

export default SettingsPage;
